import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

function resolveFeeParentKeys(rows) {
  const decodeProperty = (value) => {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };
  const propertiesByRow = new Map(rows.map((row) => {
    try {
      const parsed = JSON.parse(row.dataset.cartProperties || '{}') || {};
      const properties = Array.isArray(parsed) ? Object.fromEntries(parsed) : parsed;
      return [row, Object.fromEntries(Object.entries(properties).map(([key, value]) =>
        [decodeProperty(key), decodeProperty(value)]
      ))];
    }
    catch { return [row, {}]; }
  }));
  for (const row of rows) {
    const properties = propertiesByRow.get(row);
    if (!properties._fee_parent_variant_id || !properties._fee_parent_properties) continue;
    try {
      const parentProperties = JSON.parse(properties._fee_parent_properties);
      const parentEntries = Object.entries(parentProperties).filter(([, value]) => value !== '' && value != null);
      const parent = rows.find((candidate) => {
        if (candidate === row || candidate.dataset.cartProductId !== String(properties._fee_parent_product_id) ||
            candidate.dataset.cartVariantId !== String(properties._fee_parent_variant_id)) return false;
        const candidateProperties = propertiesByRow.get(candidate);
        const candidateEntries = Object.entries(candidateProperties).filter(([, value]) => value !== '' && value != null);
        return candidateEntries.length === parentEntries.length &&
          parentEntries.every(([key, value]) => candidateProperties[key] === value);
      });
      if (parent) row.dataset.feeParentKey = parent.dataset.key;
    } catch {}
  }
  return rows;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #duplicatePriceCleanupObserver = new MutationObserver(() => this.#scheduleDuplicateComparePriceCleanup());

  /** @returns {HTMLTableRowElement[]} */
  #getCartItemRows() {
    return resolveFeeParentKeys(Array.from(this.querySelectorAll('tr[data-key]')).filter(
      (row) => row instanceof HTMLTableRowElement
    ));
  }

  /** @param {number} line */
  #getCartItemRow(line) {
    return this.#getCartItemRows().find((row) => row.dataset.cartLine === line.toString());
  }

  /** @param {number} line */
  #getQuantitySelector(line) {
    return this.querySelector(`input[data-cart-line="${line}"]`)?.closest('cart-quantity-selector-component');
  }

  #groupLinkedFeeRows() {
    const rows = this.#getCartItemRows();
    const rowsByKey = new Map(rows.map((row) => [row.dataset.key, row]));
    const feeRowsByParentKey = new Map();
    const feeRowsByParentGroup = new Map();

    for (const row of rows) {
      const parentKey = row.dataset.feeParentKey;
      const parentGroup = row.dataset.feeParentGroup;
      const targetMap = parentKey ? feeRowsByParentKey : parentGroup ? feeRowsByParentGroup : null;
      const targetKey = parentKey || parentGroup;
      if (!targetMap || !targetKey) continue;
      targetMap.set(targetKey, [...(targetMap.get(targetKey) || []), row]);
    }

    const insertRowsAfter = (anchor, linkedRows) => {
      if (!anchor?.parentNode || linkedRows.length === 0) return;
      const fragment = document.createDocumentFragment();
      linkedRows.forEach((row) => fragment.append(row));
      anchor.parentNode.insertBefore(fragment, anchor.nextSibling);
    };

    feeRowsByParentKey.forEach((linkedRows, parentKey) => {
      insertRowsAfter(rowsByKey.get(parentKey), linkedRows);
    });

    feeRowsByParentGroup.forEach((linkedRows, parentGroup) => {
      const parentRows = rows.filter(
        (row) => row.dataset.bulkOrderGroup === parentGroup && !row.dataset.feeParentKey && !row.dataset.feeParentGroup
      );
      insertRowsAfter(parentRows.at(-1), linkedRows);
    });
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#duplicatePriceCleanupObserver.observe(this, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    this.#groupLinkedFeeRows();
    this.#scheduleDuplicateComparePriceCleanup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.#duplicatePriceCleanupObserver.disconnect();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Check minimum quantity requirement
    const quantitySelector = this.#getQuantitySelector(line);
    if (quantitySelector) {
      const minimumQuantity = parseInt(quantitySelector.dataset.minimumQuantity || '0');
      if (minimumQuantity > 0 && quantity < minimumQuantity) {
        // Revert to minimum quantity
        const quantityInput = quantitySelector.querySelector('input');
        if (quantityInput) {
          quantityInput.value = minimumQuantity.toString();
          quantitySelector.updateButtonStates?.();
        }

        // Show error message
        this.#handleCartMinimumQuantityError(line, minimumQuantity);
        return;
      }
    }

    const lineItemRow = this.#getCartItemRow(line);

    this.updateQuantity({
      line,
      lineKey: lineItemRow?.dataset.key,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const removedKey = cartItemRowToRemove.dataset.key;
    const removedGroup = cartItemRowToRemove.dataset.bulkOrderGroup;
    const groupHasOtherParents = removedGroup
      ? this.#getCartItemRows().some(
          (row) => row.dataset.bulkOrderGroup === removedGroup && row.dataset.key && row.dataset.key !== removedKey
        )
      : false;
    const linkedFeeRowsToRemove = removedKey
      ? this.#getCartItemRows().filter(
          (row) =>
            (row.dataset.feeParentKey === removedKey && !row.dataset.feeParentGroup) ||
            (removedGroup && row.dataset.feeParentGroup === removedGroup && !groupHasOtherParents)
        )
      : [];
    const linkedFeeKeysToRemove = linkedFeeRowsToRemove
      .map((row) => row.dataset.key)
      .filter((key) => key);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      lineKey: cartItemRowToRemove.dataset.key,
      keysToClear: linkedFeeKeysToRemove,
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      ...linkedFeeRowsToRemove,
      // Get all nested lines of the row to remove
      ...this.#getCartItemRows().filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.#getCartItemRows().length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.lineKey] - The cart line key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string[]} [config.keysToClear] - Extra cart line keys to remove with this line.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const extraKeysToClear = Array.isArray(config.keysToClear) ? config.keysToClear.filter((key) => key) : [];
    const changedRow = config.lineKey
      ? this.#getCartItemRows().find((row) => row.dataset.key === config.lineKey)
      : null;
    const changedGroup = changedRow?.dataset.bulkOrderGroup;
    const groupQuantity = changedGroup
      ? this.#getCartItemRows().reduce((total, row) => {
          if (row.dataset.bulkOrderGroup !== changedGroup || !row.dataset.key) return total;
          if (row.dataset.key === config.lineKey) return total + quantity;
          return total + (parseInt(row.dataset.cartQuantity || '0', 10) || 0);
        }, 0)
      : 0;
    const linkedFeeQuantityUpdates =
      config.lineKey
        ? this.#getCartItemRows()
            .filter(
              (row) =>
                row.dataset.feeQuantityMode === 'per-item' &&
                row.dataset.key &&
                (row.dataset.feeParentKey === config.lineKey ||
                  (changedGroup && row.dataset.feeParentGroup === changedGroup))
            )
            .map((row) => [
              row.dataset.key,
              changedGroup && row.dataset.feeParentGroup === changedGroup ? groupQuantity : quantity,
            ])
        : [];
    const shouldUseCartUpdate =
      !!config.lineKey && (extraKeysToClear.length > 0 || linkedFeeQuantityUpdates.length > 0);
    const bodyPayload = shouldUseCartUpdate
      ? {
          updates: {
            [config.lineKey]: quantity,
            ...Object.fromEntries(linkedFeeQuantityUpdates),
            ...Object.fromEntries(extraKeysToClear.map((key) => [key, 0])),
          },
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        }
      : {
          line: line,
          quantity: quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        };
    const body = JSON.stringify(bodyPayload);

    cartTotal?.shimmer();

    fetch(shouldUseCartUpdate ? Theme.routes.cart_update_url : `${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
        this.#groupLinkedFeeRows();

        this.#updateCartQuantitySelectorButtonStates();
        this.#scheduleDuplicateComparePriceCleanup();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelector(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles minimum quantity error for cart items.
   * @param {number} line - The line item index.
   * @param {number} minimumQuantity - The minimum required quantity.
   */
  #handleCartMinimumQuantityError = (line, minimumQuantity) => {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) return;
    if (!(cartItemErrorContainer instanceof HTMLElement)) return;

    // Get error message template from theme translations or use default
    const errorMessage = Theme.translations?.quantity_error_min
      ? Theme.translations.quantity_error_min.replace('{{ minimum }}', minimumQuantity.toString())
      : `Minimum quantity required: ${minimumQuantity}`;

    cartItemError.textContent = errorMessage;
    cartItemErrorContainer.classList.remove('hidden');

    // Hide error after 10 seconds (same duration as other errors)
    setTimeout(() => {
      cartItemErrorContainer.classList.add('hidden');
    }, 10000);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#groupLinkedFeeRows();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      await morphSection(this.sectionId, cartItemsHtml);
      this.#groupLinkedFeeRows();

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleDuplicateComparePriceCleanup();
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#groupLinkedFeeRows();
      this.#scheduleDuplicateComparePriceCleanup();
    }
  };

  #scheduleDuplicateComparePriceCleanup() {
    if (!this.#hasYmqPriceHooks()) return;

    this.#hideDuplicateComparePrices();
    requestAnimationFrame(() => this.#hideDuplicateComparePrices());
    setTimeout(() => this.#hideDuplicateComparePrices(), 100);
    setTimeout(() => this.#hideDuplicateComparePrices(), 500);
  }

  #hasYmqPriceHooks() {
    return Boolean(
      this.querySelector('.ymq-b2b-price-hidden, .ymq-b2b-line-through, [ymq-b2b-item-key], [ymq-b2b-cart-total-price]')
    );
  }

  #hideDuplicateComparePrices() {
    const comparePrices = this.querySelectorAll(
      '.cart-items__unit-price-wrapper .ymq-b2b-line-through, .cart-items__price .ymq-b2b-line-through'
    );

    for (const comparePrice of comparePrices) {
      if (!(comparePrice instanceof HTMLElement)) continue;

      comparePrice.hidden = false;
      comparePrice.removeAttribute('aria-hidden');

      const compareText = this.#normalizePriceText(comparePrice.textContent || '');
      const priceGroup = comparePrice.closest('.cart-items__unit-price-wrapper, .cart-items__price');
      if (!compareText || !priceGroup) continue;

      const matchingPrice = [...priceGroup.querySelectorAll('span, text-component')]
        .filter((element) => element instanceof HTMLElement)
        .filter((element) => !element.contains(comparePrice) && !comparePrice.contains(element))
        .filter((element) => !element.closest('.ymq-b2b-line-through, .visually-hidden'))
        .some((element) => this.#normalizePriceText(element.textContent || '') === compareText);

      if (matchingPrice) {
        comparePrice.hidden = true;
        comparePrice.setAttribute('aria-hidden', 'true');
      }
    }
  }

  #normalizePriceText(text) {
    return text.replace(/\s+/g, '').trim();
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + item.quantity);
    }

    for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
      const variantId = selector.getAttribute('data-variant-id');
      const quantity = quantitiesByVariant.get(variantId) || 0;
      if ('setCartQuantity' in selector && typeof selector.setCartQuantity === 'function') {
        selector.setCartQuantity(quantity);
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
